import { useState } from 'react';
import InitialStockAdminService from '../../../services/inventory/InitialStockAdminService';
import useApiErrorHandler from '../../useApiErrorHandler';
import useAlerts from '../../useAlerts';
import {
  InitialStockAdminRow,
  SelectedStockRow,
} from '../../../types/inventory/initialStockAdmin/InitialStockAdminTypes';

const rowKey = (row: Pick<InitialStockAdminRow, 'productId' | 'branchId'>) =>
  `${row.productId}-${row.branchId}`;

export const useInitialStockAdmin = () => {
  const { handle } = useApiErrorHandler();
  const { show: showAlert } = useAlerts();

  const [rows, setRows] = useState<InitialStockAdminRow[]>([]);
  const [paginator, setPaginator] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [selected, setSelected] = useState<Map<string, SelectedStockRow>>(new Map());

  const search = async (params: {
    branchId?: number | null;
    term?: string;
    categoryId?: number | null;
    onlyNegative?: boolean;
    page?: number;
    size?: number;
  }) => {
    setIsLoading(true);
    try {
      const data = await InitialStockAdminService.search(params);
      setRows(data?.content ?? []);
      setPaginator(data);
    } catch (error: any) {
      handle(error);
    } finally {
      setIsLoading(false);
    }
  };

  const isSelected = (row: InitialStockAdminRow) => selected.has(rowKey(row));

  const toggleSelected = (row: InitialStockAdminRow) => {
    setSelected((prev) => {
      const next = new Map(prev);
      const key = rowKey(row);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, {
          ...row,
          newQuantity: row.stockQuantity,
          newUnitCost: row.averageCost,
        });
      }
      return next;
    });
  };

  const removeSelected = (row: Pick<InitialStockAdminRow, 'productId' | 'branchId'>) => {
    setSelected((prev) => {
      const next = new Map(prev);
      next.delete(rowKey(row));
      return next;
    });
  };

  const updateSelectedValue = (
    row: Pick<InitialStockAdminRow, 'productId' | 'branchId'>,
    field: 'newQuantity' | 'newUnitCost',
    value: number
  ) => {
    setSelected((prev) => {
      const key = rowKey(row);
      const current = prev.get(key);
      if (!current) return prev;
      const next = new Map(prev);
      next.set(key, { ...current, [field]: value });
      return next;
    });
  };

  const clearSelected = () => setSelected(new Map());

  const saveOverride = async (): Promise<boolean> => {
    const items = Array.from(selected.values()).map((row) => ({
      productId: row.productId,
      branchId: row.branchId,
      quantity: row.newQuantity,
      unitCost: row.newUnitCost,
    }));

    if (items.length === 0) return false;

    setIsSaving(true);
    try {
      const response = await InitialStockAdminService.override({ items });
      showAlert(
        response?.type ?? 'success',
        response?.title ?? 'Listo',
        response?.message ?? 'Inventario corregido correctamente.'
      );
      clearSelected();
      return true;
    } catch (error: any) {
      handle(error);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    rows,
    paginator,
    isLoading,
    isSaving,
    selected,
    selectedList: Array.from(selected.values()),
    isSelected,
    toggleSelected,
    removeSelected,
    updateSelectedValue,
    clearSelected,
    search,
    saveOverride,
  };
};

export default useInitialStockAdmin;
